import React, { useState, useRef, useEffect } from 'react'
import Button from '../../ui/button/Button'
import { Plus } from 'lucide-react'
import KanbanOptions from './KanbanOptions'
import { useCreate } from '../../../context/CreateRegisterContext'
import { Task, useBusiness } from '../../../context/BusinessContext'
import TaskPreview from './TaskPreview'
import KanbanEmpty from './KanbanEmpty'

export interface Column {
    id: string
    title: string
    color: string
}

interface KanbanProps {
    tab: string
    columns: Column[]
    initialTasks: Task[]
    handleCreateStep: () => void
    handleEditStep: () => void
    handleDeleteStep: () => void
}

const Kanban: React.FC<KanbanProps> = ({
    tab,
    columns,
    initialTasks,
    handleCreateStep,
    handleEditStep,
    handleDeleteStep
}) => {
    const {
        business,
        setBusiness,
        filteredBusiness,
        setFilteredBusiness,
        setCurrentTab,
        setCurrentStep,
        setOpenDetail,
        moveBusiness
    } = useBusiness()
    const [tasks, setTasks] = useState<Task[]>(initialTasks)
    const [draggedOverColumn, setDraggedOverColumn] = useState<string | null>(null)
    const [draggingTask, setDraggingTask] = useState<boolean>(false)
    const { setOpenBusiness } = useCreate()

    // Estados para arrastar o scroll horizontal
    const [isDown, setIsDown] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)
    const scrollRef = useRef<HTMLDivElement>(null)

    // Eventos para drag and drop das tasks
    const onDragStart = (e: React.DragEvent<HTMLDivElement>, id: number) => {
        e.stopPropagation()
        e.dataTransfer.setData('id', id.toString())
        setDraggingTask(true)
    }

    const onDragOver = (e: React.DragEvent<HTMLDivElement>, columnId: string) => {
        e.preventDefault()
        if (draggedOverColumn !== columnId) {
            setDraggedOverColumn(columnId)
        }
    }

    const onDrop = (e: React.DragEvent<HTMLDivElement>, stepID: string) => {
        const id = e.dataTransfer.getData('id') // ID da task que foi arrastada
        const updatedTasks = tasks.map(task => {
            if (task.id == id) {
                return { ...task, stepID }
            }
            return task
        })

        //* Atualiza banco de dados e contexto
        moveBusiness(parseInt(id), tab, parseInt(stepID), updatedTasks)
    }

    // Funções para "drag to scroll"
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest('.no-drag-scroll')) return

        setIsDown(true)
        if (scrollRef.current) {
            setStartX(e.pageX - scrollRef.current.offsetLeft)
            setScrollLeft(scrollRef.current.scrollLeft)
        }
    }

    const handleMouseLeave = () => setIsDown(false)
    const handleMouseUp = () => setIsDown(false)
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDown || !scrollRef.current) return
        e.preventDefault()
        const x = e.pageX - scrollRef.current.offsetLeft
        const walk = (x - startX) * 1.5
        scrollRef.current.scrollLeft = scrollLeft - walk
    }

    // 🔥 sincroniza com mudanças externas (ex: filtros aplicados)
    useEffect(() => {
        setTasks(initialTasks)
    }, [initialTasks])

    return (
        <div
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            className='overflow-x-auto cursor-grab active:cursor-grabbing no-scrollbar min-h-[80vh]'
        >
            {columns.length > 0 ? (
                <div className='flex gap-2 text-gray-800 dark:text-white/90 w-max inset-0'>
                    {columns.map((column, index) => (
                        <div
                            key={column.id}
                            className={`no-drag-scroll rounded-xl border p-4 h-full w-[18rem] border-t-[4px] ${
                                draggedOverColumn === column.id
                                    ? 'border-l-gray-200 border-r-gray-200 border-b-gray-200 bg-brand-50 dark:bg-brand-500/[0.12]'
                                    : 'border-l-gray-200 border-r-gray-200 border-b-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]'
                            }`}
                            onDragOver={e => onDragOver(e, column.id)}
                            onDrop={e => onDrop(e, column.id)}
                            style={{
                                borderTopColor: column.color
                            }}
                        >
                            <div className='relative'>
                                <div className='flex items-center justify-between gap-2 mb-4'>
                                    <div>
                                        <h2 className='font-extrabold text-lg' style={{ color: column.color }}>
                                            {column.title}
                                        </h2>
                                        {/* Totalizadores (%) */}
                                        <p className='text-xs'>
                                            {tasks.filter(task => task.stepID === column.id).length}{' '}
                                            {tasks.filter(task => task.stepID === column.id).length == 1
                                                ? 'item '
                                                : 'itens '}
                                            <span style={{ color: column.color }}>
                                                (
                                                {(
                                                    (tasks.filter(task => task.stepID === column.id).length /
                                                        tasks.length) *
                                                    100
                                                ).toFixed(1)}
                                                %)
                                            </span>
                                        </p>
                                    </div>
                                    <KanbanOptions
                                        handleCreateTask={() => {
                                            setCurrentTab(tab)
                                            setCurrentStep(column)
                                            setOpenBusiness(true)
                                        }}
                                        column={column}
                                        handleCreateStep={() => {
                                            setCurrentStep(column)
                                            handleCreateStep()
                                        }}
                                        handleEditStep={() => {
                                            setCurrentStep(column)
                                            handleEditStep()
                                        }}
                                        handleDeleteStep={() => {
                                            setCurrentStep(column)
                                            handleDeleteStep()
                                        }}
                                    />
                                </div>
                                <div className='space-y-2 text-sm'>
                                    {tasks
                                        .filter(task => task.stepID === column.id)
                                        .map(task => (
                                            <div
                                                key={task.id}
                                                draggable
                                                onDragStart={e => onDragStart(e, parseInt(task.id))}
                                                onClick={() => {
                                                    // Business Context
                                                    setOpenDetail({
                                                        open: true,
                                                        data: task
                                                    })
                                                }}
                                                className={`no-drag-scroll p-4 rounded-3xl cursor-pointer ${
                                                    task.isExpired
                                                        ? 'border border-red-500 dark:border-red-500 hover:bg-red-50 dark:hover:bg-red-500/[0.12] hover:border-red-500 dark:hover:border-red-500'
                                                        : 'border border-gray-200 dark:border-gray-800 hover:bg-brand-50 dark:hover:bg-brand-500/[0.12] hover:border-brand-500 dark:hover:border-brand-500'
                                                }`}
                                            >
                                                <TaskPreview task={task} />
                                            </div>
                                        ))}
                                    {draggedOverColumn === column.id && draggingTask && (
                                        <div className='no-drag-scroll p-4 rounded-lg border-2 border-dashed border-brand-500 bg-white dark:bg-gray-900 '>
                                            Solte aqui...
                                        </div>
                                    )}
                                    <Button
                                        size='sm'
                                        variant='link'
                                        onClick={() => {
                                            setCurrentTab(tab)
                                            setCurrentStep(column)
                                            setOpenBusiness(true)
                                        }}
                                    >
                                        <div className='no-drag-scroll flex items-center gap-1 hover:text-brand-500 text-sm'>
                                            <Plus className='w-4 h-4' />
                                            <p>Adicionar</p>
                                        </div>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <KanbanEmpty handleCreateStep={handleCreateStep} />
            )}
        </div>
    )
}

export default Kanban
